import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import MoneyInput from "../../../../Components/Common/MoneyInput";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import { entityFormKey } from "../../../../helpers/entityEditForm";
import { HELP_ARTICLE_SERVICE_PACKAGES } from "../../../../helpers/helpCenter";
import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import {
  createServicePackage,
  deleteServicePackage,
  getServicePackage,
  getServicePackages,
  ServicePackage,
  updateServicePackage,
} from "../../../../services/servicePackageService";
import { getServices, Service } from "../../../../services/serviceService";
import { getUnits, Unit } from "../../../../services/unitService";
import { getLastTenantId } from "../../../../helpers/identity_storage";
import {
  getPackageSuggestedDiscountPercent,
  PACKAGE_DISCOUNT_OPTIONS,
  PackageDiscountPercent,
  setPackageSuggestedDiscountPercent,
} from "../../../../helpers/packageDiscountPreference";
import { setPendingPackageSale } from "../../../../helpers/pendingPackageSaleStorage";
import {
  getSettings,
  updateSettings,
} from "../../../../services/settingsService";
import { PaginationMeta } from "../../../../types/api";
import { CreateServicePackagePayload } from "../../../../types/servicePackage";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;
const CATALOG_PAGE_SIZE = 100;

const isPackageDiscountPercent = (
  value: number
): value is PackageDiscountPercent =>
  (PACKAGE_DISCOUNT_OPTIONS as readonly number[]).includes(value);

const resolvePackageSavingsPercent = (pkg: ServicePackage): number | null => {
  const servicePrice = pkg.service?.price;
  if (servicePrice == null || servicePrice <= 0 || pkg.sessions_count < 2) {
    return null;
  }

  const retailTotal = servicePrice * pkg.sessions_count;
  if (retailTotal <= 0 || pkg.price >= retailTotal) {
    return null;
  }

  return Math.round(((retailTotal - pkg.price) / retailTotal) * 100);
};

interface ServicePackageFormValues {
  unit_id: string;
  service_id: string;
  name: string;
  sessions_count: number;
  price_display: string;
  session_unit_price_display: string;
  validity_days: string;
  is_active: boolean;
}

const emptyFormValues = (): ServicePackageFormValues => ({
  unit_id: "",
  service_id: "",
  name: "",
  sessions_count: 10,
  price_display: "",
  session_unit_price_display: "",
  validity_days: "",
  is_active: true,
});

const mapPackageToFormValues = (
  pkg: ServicePackage,
  locale: string
): ServicePackageFormValues => ({
  unit_id: String(pkg.unit_id),
  service_id: String(pkg.service_id),
  name: pkg.name,
  sessions_count: pkg.sessions_count,
  price_display: formatCentsToMoneyInput(pkg.price, locale),
  session_unit_price_display: formatCentsToMoneyInput(
    pkg.session_unit_price,
    locale
  ),
  validity_days: pkg.validity_days != null ? String(pkg.validity_days) : "",
  is_active: pkg.is_active,
});

const buildPackagePayload = (
  values: ServicePackageFormValues,
  locale: string
): CreateServicePackagePayload => ({
  unit_id: Number(values.unit_id),
  service_id: Number(values.service_id),
  name: values.name.trim(),
  sessions_count: Number(values.sessions_count),
  price: parseMoneyInputToCents(values.price_display, locale),
  session_unit_price: parseMoneyInputToCents(
    values.session_unit_price_display,
    locale
  ),
  validity_days:
    values.validity_days.trim() !== ""
      ? Number(values.validity_days)
      : null,
  is_active: values.is_active,
});

const ServicePackagesList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const createPrefillRef = useRef<{ unit_id: string; service_id: string } | null>(
    null
  );

  document.title = t("studio.servicePackages.pageTitle");

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<ServicePackage | null>(
    null
  );
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ServicePackage | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const lastAutoNameRef = useRef("");
  const tenantId = getLastTenantId() ?? 0;
  const [suggestedDiscountPercent, setSuggestedDiscountPercent] =
    useState<PackageDiscountPercent>(() =>
      getPackageSuggestedDiscountPercent(tenantId)
    );

  useEffect(() => {
    let cancelled = false;

    const loadSuggestedDiscount = async () => {
      const cached = getPackageSuggestedDiscountPercent(tenantId);
      setSuggestedDiscountPercent(cached);

      try {
        const response = await getSettings();
        if (cancelled) {
          return;
        }

        const fromApi = Number(response.data?.package_suggested_discount_percent);
        if (isPackageDiscountPercent(fromApi)) {
          setSuggestedDiscountPercent(fromApi);
          if (tenantId > 0) {
            setPackageSuggestedDiscountPercent(tenantId, fromApi);
          }
        }
      } catch {
        // Keep local cache when settings cannot be loaded.
      }
    };

    void loadSuggestedDiscount();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const persistSuggestedDiscount = async (percent: PackageDiscountPercent) => {
    setSuggestedDiscountPercent(percent);
    if (tenantId > 0) {
      setPackageSuggestedDiscountPercent(tenantId, percent);
    }

    try {
      await updateSettings({ package_suggested_discount_percent: percent });
      toast.success(t("studio.servicePackages.toast.discountSaveSuccess"));
    } catch (error: any) {
      toast.error(
        error?.data ||
          t("studio.servicePackages.toast.discountSaveError")
      );
    }
  };

  const notAvailable = t("studio.servicePackages.common.notAvailable");

  const buildAutoPackageName = useCallback(
    (sessions: number, serviceName: string) =>
      t("studio.servicePackages.fields.name.autoPattern", {
        sessions,
        serviceName,
      }),
    [t]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(
          t("studio.servicePackages.validation.unitRequired")
        ),
        service_id: Yup.string().required(
          t("studio.servicePackages.validation.serviceRequired")
        ),
        name: Yup.string()
          .trim()
          .required(t("studio.servicePackages.validation.nameRequired")),
        sessions_count: Yup.number()
          .typeError(t("studio.servicePackages.validation.sessionsType"))
          .integer(t("studio.servicePackages.validation.sessionsType"))
          .min(2, t("studio.servicePackages.validation.sessionsMin"))
          .required(t("studio.servicePackages.validation.sessionsRequired")),
        price_display: Yup.string()
          .required(t("studio.servicePackages.validation.priceRequired"))
          .test(
            "price-valid",
            t("studio.servicePackages.validation.priceInvalid"),
            (value) =>
              parseMoneyInputToCents(value || "", i18n.language) >= 0
          ),
        session_unit_price_display: Yup.string()
          .required(t("studio.servicePackages.validation.sessionPriceRequired"))
          .test(
            "session-price-valid",
            t("studio.servicePackages.validation.sessionPriceInvalid"),
            (value) =>
              parseMoneyInputToCents(value || "", i18n.language) >= 0
          ),
        validity_days: Yup.string().test(
          "validity-valid",
          t("studio.servicePackages.validation.validityMin"),
          (value) => {
            if (!value || value.trim() === "") {
              return true;
            }
            const parsed = Number(value);
            return Number.isInteger(parsed) && parsed >= 1;
          }
        ),
      }),
    [t, i18n.language]
  );

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, unitFilter, statusFilter]);

  const loadUnits = useCallback(async () => {
    try {
      const response = await getUnits({ per_page: CATALOG_PAGE_SIZE });
      setUnits(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.servicePackages.toast.loadError"));
    }
  }, [t]);

  const loadServicesForUnit = useCallback(
    async (unitId: number) => {
      try {
        const response = await getServices({
          per_page: CATALOG_PAGE_SIZE,
          unit_id: unitId,
        });
        setServices(response.data);
      } catch (error: any) {
        toast.error(error?.data || t("studio.servicePackages.toast.loadError"));
        setServices([]);
      }
    },
    [t]
  );

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getServicePackages({
        page,
        per_page: PER_PAGE,
        ...(unitFilter ? { unit_id: Number(unitFilter) } : {}),
        ...(statusFilter === "active" ? { is_active: true } : {}),
        ...(statusFilter === "inactive" ? { is_active: false } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      setPackages(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("studio.servicePackages.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, unitFilter, statusFilter, t]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const formInitialValues = useMemo(
    () =>
      editingPackage
        ? mapPackageToFormValues(editingPackage, i18n.language)
        : { ...emptyFormValues(), unit_id: unitFilter || "" },
    [editingPackage, unitFilter, i18n.language]
  );

  const formik = useFormik<ServicePackageFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      const payload = buildPackagePayload(values, i18n.language);

      try {
        if (editingPackage) {
          const response = await updateServicePackage(
            editingPackage.id,
            payload
          );
          toast.success(
            response.message || t("studio.servicePackages.toast.saveSuccess")
          );
          closeOffcanvas();
          await fetchPackages();
        } else {
          const response = await createServicePackage(payload);
          toast.success(
            response.message || t("studio.servicePackages.toast.saveSuccess")
          );
          closeOffcanvas();
          if (page !== 1) {
            setPage(1);
          } else {
            await fetchPackages();
          }

          const sellPrompt = await Swal.fire({
            title: t("studio.servicePackages.afterCreate.sellTitle"),
            text: t("studio.servicePackages.afterCreate.sellText", {
              name: response.data?.name ?? payload.name,
            }),
            icon: "success",
            showCancelButton: true,
            confirmButtonText: t("studio.servicePackages.afterCreate.sellConfirm"),
            cancelButtonText: t("studio.servicePackages.afterCreate.sellSkip"),
            confirmButtonColor: "#405189",
            cancelButtonColor: "#878a99",
          });

          if (sellPrompt.isConfirmed && response.data?.id) {
            if (tenantId > 0) {
              setPendingPackageSale(tenantId, {
                service_package_id: response.data.id,
              });
            }
            const params = new URLSearchParams({
              action: "new",
              item_type: "package",
              service_package_id: String(response.data.id),
            });
            navigate(`/orders?${params.toString()}`);
          }
        }
      } catch (error: any) {
        toast.error(error?.data || t("studio.servicePackages.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const unitId = Number(formik.values.unit_id);
    if (!Number.isFinite(unitId) || unitId <= 0) {
      setServices([]);
      return;
    }
    loadServicesForUnit(unitId);
  }, [formik.values.unit_id, loadServicesForUnit]);

  useEffect(() => {
    const serviceId = formik.values.service_id;
    const sessions = Number(formik.values.sessions_count);
    const service = services.find((item) => String(item.id) === serviceId);

    if (!service || !Number.isFinite(sessions) || sessions < 2) {
      return;
    }

    const autoName = buildAutoPackageName(sessions, service.name);
    const currentName = formik.values.name.trim();
    const shouldUpdate =
      currentName === "" || currentName === lastAutoNameRef.current;

    if (shouldUpdate && currentName !== autoName) {
      formik.setFieldValue("name", autoName);
      lastAutoNameRef.current = autoName;
    } else if (shouldUpdate) {
      lastAutoNameRef.current = autoName;
    }
    // Intentionally depends on service/sessions — not full formik object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    buildAutoPackageName,
    formik.values.service_id,
    formik.values.sessions_count,
    services,
  ]);

  const syncSessionUnitPrice = useCallback(() => {
    const sessions = Number(formik.values.sessions_count);
    const priceCents = parseMoneyInputToCents(
      formik.values.price_display,
      i18n.language
    );
    if (!Number.isFinite(sessions) || sessions < 2 || priceCents < 0) {
      return;
    }
    const unitCents = Math.floor(priceCents / sessions);
    formik.setFieldValue(
      "session_unit_price_display",
      formatCentsToMoneyInput(unitCents, i18n.language)
    );
  }, [formik, i18n.language]);

  const applySuggestedDiscount = useCallback(() => {
    const service = services.find(
      (item) => String(item.id) === formik.values.service_id
    );
    const sessions = Number(formik.values.sessions_count);
    if (!service || !Number.isFinite(sessions) || sessions < 2) {
      return;
    }

    const retailTotalCents = service.price * sessions;
    const discountedCents = Math.max(
      0,
      Math.round(
        (retailTotalCents * (100 - suggestedDiscountPercent)) / 100
      )
    );
    formik.setFieldValue(
      "price_display",
      formatCentsToMoneyInput(discountedCents, i18n.language)
    );
    const unitCents = Math.floor(discountedCents / sessions);
    formik.setFieldValue(
      "session_unit_price_display",
      formatCentsToMoneyInput(unitCents, i18n.language)
    );
    formik.setFieldTouched("price_display", true);
  }, [formik, i18n.language, services, suggestedDiscountPercent]);

  const canSuggestDiscount = useMemo(() => {
    const service = services.find(
      (item) => String(item.id) === formik.values.service_id
    );
    const sessions = Number(formik.values.sessions_count);
    return Boolean(
      service &&
        service.price > 0 &&
        Number.isFinite(sessions) &&
        sessions >= 2
    );
  }, [formik.values.service_id, formik.values.sessions_count, services]);

  const packagePriceHint = useMemo(() => {
    const service = services.find(
      (item) => String(item.id) === formik.values.service_id
    );
    if (!service) {
      return null;
    }

    const sessions = Number(formik.values.sessions_count);
    const packagePriceCents = parseMoneyInputToCents(
      formik.values.price_display,
      i18n.language
    );

    if (
      !Number.isFinite(sessions) ||
      sessions < 2 ||
      !Number.isFinite(packagePriceCents) ||
      packagePriceCents < 0
    ) {
      return null;
    }

    const retailTotalCents = service.price * sessions;
    const deltaCents = retailTotalCents - packagePriceCents;

    if (deltaCents === 0) {
      return {
        kind: "equal" as const,
        retailTotal: formatMoney(retailTotalCents),
      };
    }

    if (deltaCents > 0) {
      return {
        kind: "savings" as const,
        amount: formatMoney(deltaCents),
        retailTotal: formatMoney(retailTotalCents),
      };
    }

    return {
      kind: "overpriced" as const,
      amount: formatMoney(Math.abs(deltaCents)),
      retailTotal: formatMoney(retailTotalCents),
    };
  }, [
    formatMoney,
    formik.values.price_display,
    formik.values.service_id,
    formik.values.sessions_count,
    i18n.language,
    services,
  ]);

  const closeOffcanvas = () => {
    setOffcanvasOpen(false);
    setEditingPackage(null);
    setFormLoading(false);
    lastAutoNameRef.current = "";
  };

  const openCreate = () => {
    setEditingPackage(null);
    setFormLoading(false);
    lastAutoNameRef.current = "";
    setOffcanvasOpen(true);
  };

  useEffect(() => {
    if (searchParams.get("action") !== "create") {
      return;
    }

    createPrefillRef.current = {
      unit_id: searchParams.get("unit_id") || "",
      service_id: searchParams.get("service_id") || "",
    };
    openCreate();

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("action");
    nextParams.delete("unit_id");
    nextParams.delete("service_id");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const prefill = createPrefillRef.current;
    if (!offcanvasOpen || editingPackage || !prefill?.unit_id) {
      return;
    }

    formik.setFieldValue("unit_id", prefill.unit_id);
    // Prefill only when canvas opens — not on every formik identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offcanvasOpen, editingPackage]);

  useEffect(() => {
    const prefill = createPrefillRef.current;
    if (!prefill?.service_id || services.length === 0) {
      return;
    }

    const exists = services.some(
      (service) => String(service.id) === prefill.service_id
    );
    if (!exists) {
      return;
    }

    formik.setFieldValue("service_id", prefill.service_id);
    createPrefillRef.current = null;
    // Prefill once services load — not on every formik identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  const openEdit = async (pkg: ServicePackage) => {
    setEditingPackage(null);
    setFormLoading(true);
    setOffcanvasOpen(true);

    try {
      const response = await getServicePackage(pkg.id);
      const loaded = response.data;
      setEditingPackage(loaded);
      const serviceName = loaded.service?.name ?? "";
      const autoName = serviceName
        ? buildAutoPackageName(loaded.sessions_count, serviceName)
        : "";
      lastAutoNameRef.current = loaded.name === autoName ? autoName : "";
    } catch (error: any) {
      toast.error(error?.data || t("studio.servicePackages.toast.loadError"));
      closeOffcanvas();
    } finally {
      setFormLoading(false);
    }
  };

  const requestDelete = async (pkg: ServicePackage) => {
    if (pkg.is_active) {
      const confirmation = await Swal.fire({
        title: t("studio.servicePackages.delete.activeConfirmTitle"),
        text: t("studio.servicePackages.delete.activeConfirmText"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("studio.servicePackages.actions.confirmDelete"),
        cancelButtonText: t("studio.servicePackages.actions.cancel"),
        confirmButtonColor: "#f06548",
        cancelButtonColor: "#878a99",
        focusCancel: true,
      });

      if (!confirmation.isConfirmed) {
        return;
      }
    }

    setDeleteTarget(pkg);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await deleteServicePackage(deleteTarget.id);
      toast.success(
        response.message || t("studio.servicePackages.toast.deleteSuccess")
      );
      setDeleteModal(false);
      setDeleteTarget(null);
      const isLastOnPage = packages.length === 1 && page > 1;
      if (isLastOnPage) {
        setPage(page - 1);
      } else {
        await fetchPackages();
      }
    } catch (error: any) {
      if (error?.code === "PACKAGE_HAS_ACTIVE_CREDITS") {
        setDeleteModal(false);
        const clients = Array.isArray(error?.clients) ? error.clients : [];
        const escapeHtml = (value: string) =>
          value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
        const clientsHtml =
          clients.length > 0
            ? `<p class="mb-2 text-start">${escapeHtml(
                t("studio.servicePackages.delete.hasCreditsClientsIntro")
              )}</p><ul class="text-start mb-0">${clients
                .map(
                  (client: {
                    id: number;
                    name: string;
                    sessions_remaining: number;
                  }) =>
                    `<li><button type="button" class="btn btn-link p-0 align-baseline" data-client-nav="${Number(
                      client.id
                    )}">${escapeHtml(
                      client.name || String(client.id)
                    )}</button> — ${escapeHtml(
                      t("studio.servicePackages.delete.hasCreditsClientSessions", {
                        count: client.sessions_remaining,
                      })
                    )}</li>`
                )
                .join("")}</ul>`
            : `<p class="mb-0">${escapeHtml(
                error?.data || t("studio.servicePackages.delete.hasCreditsText")
              )}</p>`;

        const confirmation = await Swal.fire({
          title: t("studio.servicePackages.delete.hasCreditsTitle"),
          html: clientsHtml,
          icon: "info",
          showCancelButton: true,
          confirmButtonText: t(
            "studio.servicePackages.delete.deactivateInstead"
          ),
          cancelButtonText: t("studio.servicePackages.actions.cancel"),
          confirmButtonColor: "#405189",
          cancelButtonColor: "#878a99",
          didOpen: (popup) => {
            popup.querySelectorAll<HTMLElement>("[data-client-nav]").forEach((el) => {
              el.addEventListener("click", (event) => {
                event.preventDefault();
                const clientId = el.getAttribute("data-client-nav");
                if (!clientId) {
                  return;
                }
                Swal.close();
                navigate(`/clients/${clientId}`);
              });
            });
          },
        });

        if (confirmation.isConfirmed) {
          try {
            await updateServicePackage(deleteTarget.id, { is_active: false });
            toast.success(
              t("studio.servicePackages.toast.deactivateSuccess")
            );
            setDeleteTarget(null);
            await fetchPackages();
          } catch (deactivateError: any) {
            toast.error(
              deactivateError?.data ||
                t("studio.servicePackages.toast.deactivateError")
            );
          }
        }
      } else {
        toast.error(
          error?.data || t("studio.servicePackages.toast.deleteError")
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resolveUnitName = (pkg: ServicePackage): string =>
    pkg.unit?.name ??
    units.find((unit) => unit.id === pkg.unit_id)?.name ??
    notAvailable;

  const resolveServiceName = (pkg: ServicePackage): string =>
    pkg.service?.name ?? notAvailable;

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("studio.servicePackages.breadcrumb.title")}
          pageTitle={t("studio.servicePackages.breadcrumb.pageTitle")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                <h4 className="card-title mb-0 flex-grow-1">
                  {t("studio.servicePackages.card.title")}
                </h4>
                <HelpArticleLink
                  articlePath={HELP_ARTICLE_SERVICE_PACKAGES}
                  className="align-self-center"
                />
                <Button color="primary" onClick={openCreate}>
                  <i className="ri-add-line align-bottom me-1" />
                  {t("studio.servicePackages.actions.new")}
                </Button>
              </CardHeader>
              <CardBody>
                <Row className="g-2 mb-3">
                  <Col md={4}>
                    <Input
                      type="select"
                      value={unitFilter}
                      onChange={(event) => setUnitFilter(event.target.value)}
                      aria-label={t("studio.servicePackages.filter.unit")}
                    >
                      <option value="">
                        {t("studio.servicePackages.filter.allUnits")}
                      </option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Input
                      type="select"
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                      aria-label={t("studio.servicePackages.filter.status")}
                    >
                      <option value="">
                        {t("studio.servicePackages.filter.allStatuses")}
                      </option>
                      <option value="active">
                        {t("studio.servicePackages.status.active")}
                      </option>
                      <option value="inactive">
                        {t("studio.servicePackages.status.inactive")}
                      </option>
                    </Input>
                  </Col>
                  <Col md={5}>
                    <Input
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder={t(
                        "studio.servicePackages.search.placeholder"
                      )}
                    />
                  </Col>
                </Row>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary">
                      {t("studio.servicePackages.loading")}
                    </Spinner>
                  </div>
                ) : packages.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">
                      {debouncedSearch
                        ? t("studio.servicePackages.empty.noResults", {
                            term: debouncedSearch,
                          })
                        : t("studio.servicePackages.empty.noData")}
                    </p>
                    {!debouncedSearch ? (
                      <Button color="primary" onClick={openCreate}>
                        {t("studio.servicePackages.actions.createFirst")}
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.servicePackages.table.name")}</th>
                            <th>{t("studio.servicePackages.table.unit")}</th>
                            <th>{t("studio.servicePackages.table.service")}</th>
                            <th>{t("studio.servicePackages.table.sessions")}</th>
                            <th>{t("studio.servicePackages.table.price")}</th>
                            <th>
                              {t("studio.servicePackages.table.validity")}
                            </th>
                            <th>{t("studio.servicePackages.table.status")}</th>
                            <th className="text-end">
                              {t("studio.servicePackages.table.actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {packages.map((pkg) => {
                            const savingsPercent = resolvePackageSavingsPercent(pkg);

                            return (
                            <tr key={pkg.id}>
                              <td>{pkg.name}</td>
                              <td>{resolveUnitName(pkg)}</td>
                              <td>{resolveServiceName(pkg)}</td>
                              <td>{pkg.sessions_count}</td>
                              <td>
                                <div className="d-flex flex-wrap align-items-center gap-1">
                                  <span>{formatMoney(pkg.price)}</span>
                                  {savingsPercent != null ? (
                                    <Badge
                                      color="success"
                                      className="badge-soft-success fw-normal"
                                      title={t(
                                        "studio.servicePackages.table.savingsPercentHint",
                                        { percent: savingsPercent }
                                      )}
                                    >
                                      {t(
                                        "studio.servicePackages.table.savingsPercent",
                                        { percent: savingsPercent }
                                      )}
                                    </Badge>
                                  ) : null}
                                </div>
                              </td>
                              <td>
                                {pkg.validity_days != null
                                  ? t(
                                      "studio.servicePackages.table.validityDays",
                                      { days: pkg.validity_days }
                                    )
                                  : t(
                                      "studio.servicePackages.table.validityUnlimited"
                                    )}
                              </td>
                              <td>
                                <Badge
                                  color={
                                    pkg.is_active ? "success" : "secondary"
                                  }
                                >
                                  {pkg.is_active
                                    ? t("studio.servicePackages.status.active")
                                    : t(
                                        "studio.servicePackages.status.inactive"
                                      )}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  className="me-1"
                                  title={t(
                                    "studio.servicePackages.actions.edit"
                                  )}
                                  onClick={() => openEdit(pkg)}
                                >
                                  <i className="ri-pencil-line" />
                                </Button>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  title={t(
                                    "studio.servicePackages.actions.delete"
                                  )}
                                  onClick={() => {
                                    void requestDelete(pkg);
                                  }}
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    {meta && meta.last_page > 1 && (
                      <Row className="align-items-center mt-3">
                        <Col sm={6}>
                          <div className="text-muted">
                            {t("studio.servicePackages.pagination.showing", {
                              from: meta.from ?? 0,
                              to: meta.to ?? 0,
                              total: meta.total,
                            })}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <Pagination className="pagination-separated justify-content-sm-end mb-0">
                            <PaginationItem disabled={page <= 1}>
                              <PaginationLink
                                previous
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page > 1) {
                                    setPage(page - 1);
                                  }
                                }}
                              >
                                {t(
                                  "studio.servicePackages.pagination.previous"
                                )}
                              </PaginationLink>
                            </PaginationItem>
                            <PaginationItem
                              disabled={page >= meta.last_page}
                            >
                              <PaginationLink
                                next
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page < meta.last_page) {
                                    setPage(page + 1);
                                  }
                                }}
                              >
                                {t("studio.servicePackages.pagination.next")}
                              </PaginationLink>
                            </PaginationItem>
                          </Pagination>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Offcanvas isOpen={offcanvasOpen} toggle={closeOffcanvas} direction="end">
        <OffcanvasHeader toggle={closeOffcanvas}>
          {editingPackage
            ? t("studio.servicePackages.form.editTitle")
            : t("studio.servicePackages.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">
                {t("studio.servicePackages.loading")}
              </Spinner>
            </div>
          ) : (
            <Form
              key={entityFormKey(editingPackage?.id)}
              onSubmit={formik.handleSubmit}
            >
              <div className="mb-3">
                <Label htmlFor="package-unit">
                  {t("studio.servicePackages.fields.unit.label")}
                </Label>
                <Input
                  id="package-unit"
                  name="unit_id"
                  type="select"
                  value={formik.values.unit_id}
                  onChange={(event) => {
                    formik.handleChange(event);
                    formik.setFieldValue("service_id", "");
                  }}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(formik.touched.unit_id && formik.errors.unit_id)
                  }
                >
                  <option value="">
                    {t("studio.servicePackages.fields.unit.placeholder")}
                  </option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.unit_id}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label htmlFor="package-service">
                  {t("studio.servicePackages.fields.service.label")}
                </Label>
                <Input
                  id="package-service"
                  name="service_id"
                  type="select"
                  value={formik.values.service_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={!formik.values.unit_id}
                  invalid={
                    !!(formik.touched.service_id && formik.errors.service_id)
                  }
                >
                  <option value="">
                    {t("studio.servicePackages.fields.service.placeholder")}
                  </option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.service_id}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label htmlFor="package-name">
                  {t("studio.servicePackages.fields.name.label")}
                </Label>
                <Input
                  id="package-name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t(
                    "studio.servicePackages.fields.name.placeholder"
                  )}
                  invalid={!!(formik.touched.name && formik.errors.name)}
                />
                <FormFeedback>{formik.errors.name}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label htmlFor="package-sessions">
                  {t("studio.servicePackages.fields.sessions.label")}
                </Label>
                <Input
                  id="package-sessions"
                  name="sessions_count"
                  type="number"
                  min={2}
                  value={formik.values.sessions_count}
                  onChange={formik.handleChange}
                  onBlur={() => {
                    formik.setFieldTouched("sessions_count", true);
                    syncSessionUnitPrice();
                  }}
                  invalid={
                    !!(
                      formik.touched.sessions_count &&
                      formik.errors.sessions_count
                    )
                  }
                />
                <FormFeedback>{formik.errors.sessions_count}</FormFeedback>
                <small className="text-muted">
                  {t("studio.servicePackages.fields.sessions.hint")}
                </small>
              </div>

              <div className="mb-3">
                <Label htmlFor="package-price">
                  {t("studio.servicePackages.fields.price.label")}
                </Label>
                <MoneyInput
                  id="package-price"
                  locale={i18n.language}
                  value={formik.values.price_display}
                  onChange={(value) =>
                    formik.setFieldValue("price_display", value)
                  }
                  onBlur={() => {
                    formik.setFieldTouched("price_display", true);
                    syncSessionUnitPrice();
                  }}
                  placeholder={t(
                    "studio.servicePackages.fields.price.placeholder"
                  )}
                  invalid={
                    !!(
                      formik.touched.price_display &&
                      formik.errors.price_display
                    )
                  }
                />
                {formik.touched.price_display && formik.errors.price_display ? (
                  <div className="invalid-feedback d-block">
                    {formik.errors.price_display}
                  </div>
                ) : null}
                {canSuggestDiscount ? (
                  <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                    <Input
                      type="select"
                      bsSize="sm"
                      style={{ maxWidth: 110 }}
                      value={suggestedDiscountPercent}
                      aria-label={t(
                        "studio.servicePackages.fields.price.discountPercentLabel"
                      )}
                      onChange={(event) => {
                        const next = Number(event.target.value);
                        if (
                          !(PACKAGE_DISCOUNT_OPTIONS as readonly number[]).includes(
                            next
                          )
                        ) {
                          return;
                        }
                        const percent = next as PackageDiscountPercent;
                        void persistSuggestedDiscount(percent);
                      }}
                    >
                      {PACKAGE_DISCOUNT_OPTIONS.map((percent) => (
                        <option key={percent} value={percent}>
                          −{percent}%
                        </option>
                      ))}
                    </Input>
                    <Button
                      color="soft-primary"
                      size="sm"
                      type="button"
                      onClick={applySuggestedDiscount}
                    >
                      {t("studio.servicePackages.fields.price.suggestDiscount", {
                        percent: suggestedDiscountPercent,
                      })}
                    </Button>
                  </div>
                ) : null}
                {packagePriceHint?.kind === "savings" ? (
                  <small className="text-success d-block mt-1">
                    {t("studio.servicePackages.fields.price.savingsHint", {
                      amount: packagePriceHint.amount,
                      retail: packagePriceHint.retailTotal,
                    })}
                  </small>
                ) : null}
                {packagePriceHint?.kind === "overpriced" ? (
                  <small className="text-warning d-block mt-1">
                    {t("studio.servicePackages.fields.price.overpricedHint", {
                      amount: packagePriceHint.amount,
                      retail: packagePriceHint.retailTotal,
                    })}
                  </small>
                ) : null}
                {packagePriceHint?.kind === "equal" ? (
                  <small className="text-muted d-block mt-1">
                    {t("studio.servicePackages.fields.price.equalHint", {
                      retail: packagePriceHint.retailTotal,
                    })}
                  </small>
                ) : null}
              </div>

              <div className="mb-3">
                <Label htmlFor="package-session-price">
                  {t("studio.servicePackages.fields.sessionPrice.label")}
                </Label>
                <MoneyInput
                  id="package-session-price"
                  locale={i18n.language}
                  value={formik.values.session_unit_price_display}
                  onChange={(value) =>
                    formik.setFieldValue("session_unit_price_display", value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched("session_unit_price_display", true)
                  }
                  placeholder={t(
                    "studio.servicePackages.fields.sessionPrice.placeholder"
                  )}
                  invalid={
                    !!(
                      formik.touched.session_unit_price_display &&
                      formik.errors.session_unit_price_display
                    )
                  }
                />
                {formik.touched.session_unit_price_display &&
                formik.errors.session_unit_price_display ? (
                  <div className="invalid-feedback d-block">
                    {formik.errors.session_unit_price_display}
                  </div>
                ) : null}
                <small className="text-muted">
                  {t("studio.servicePackages.fields.sessionPrice.hint")}
                </small>
              </div>

              <div className="mb-3">
                <Label htmlFor="package-validity">
                  {t("studio.servicePackages.fields.validity.label")}
                </Label>
                <Input
                  id="package-validity"
                  name="validity_days"
                  type="number"
                  min={1}
                  value={formik.values.validity_days}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t(
                    "studio.servicePackages.fields.validity.placeholder"
                  )}
                  invalid={
                    !!(
                      formik.touched.validity_days &&
                      formik.errors.validity_days
                    )
                  }
                />
                <FormFeedback>{formik.errors.validity_days}</FormFeedback>
                <small className="text-muted">
                  {t("studio.servicePackages.fields.validity.hint")}
                </small>
              </div>

              <div className="form-check form-switch mb-4">
                <Input
                  id="package-active"
                  name="is_active"
                  type="checkbox"
                  className="form-check-input"
                  checked={formik.values.is_active}
                  onChange={formik.handleChange}
                />
                <Label className="form-check-label" htmlFor="package-active">
                  {t("studio.servicePackages.fields.isActive.label")}
                </Label>
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <Button
                  color="light"
                  type="button"
                  onClick={closeOffcanvas}
                  disabled={submitting}
                >
                  {t("studio.servicePackages.actions.cancel")}
                </Button>
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? (
                    <Spinner size="sm" className="me-1" />
                  ) : null}
                  {t("studio.servicePackages.actions.save")}
                </Button>
              </div>
            </Form>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal
        isOpen={deleteModal}
        toggle={() => {
          if (!submitting) {
            setDeleteModal(false);
            setDeleteTarget(null);
          }
        }}
        centered
      >
        <ModalBody className="text-center p-4">
          <div className="avatar-md mx-auto mb-3">
            <div className="avatar-title bg-danger-subtle text-danger rounded-circle fs-24">
              <i className="ri-delete-bin-line" />
            </div>
          </div>
          <h5 className="mb-2">{t("studio.servicePackages.delete.title")}</h5>
          <p className="text-muted mb-4">
            {t("studio.servicePackages.delete.message", {
              name: deleteTarget?.name ?? "",
            })}
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <Button
              color="light"
              onClick={() => {
                setDeleteModal(false);
                setDeleteTarget(null);
              }}
              disabled={submitting}
            >
              {t("studio.servicePackages.actions.cancel")}
            </Button>
            <Button
              color="danger"
              onClick={confirmDelete}
              disabled={submitting}
            >
              {t("studio.servicePackages.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ServicePackagesList;
